/**
 * TimeOptionList — the 15-minute times the time field offers under its input.
 * Opens scrolled to the chosen time, or to the early evening when there is
 * none yet, since that is when most workshops start.
 */
import { useEffect, useRef } from 'react';

import { formatTimeLabel } from './date-time-input';
import { PICKER_POPOVER } from './picker-popover-style';
import { keepInputFocus } from './use-picker-field';

/** Where an empty time field's list opens. */
const DEFAULT_SCROLL_TIME = '17:00';

export function TimeOptionList({
  id,
  options,
  value,
  active,
  onPick,
}: {
  id: string;
  options: string[];
  value: string;
  /** The keyboard-highlighted option, or null. */
  active: string | null;
  onPick: (value: string) => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const anchor = active ?? (value || DEFAULT_SCROLL_TIME);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-time="${anchor}"]`);
    // jsdom has no layout, so it has no scrollIntoView either.
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [anchor]);

  return (
    <ul
      id={id}
      ref={listRef}
      role="listbox"
      onMouseDown={keepInputFocus}
      style={{ ...PICKER_POPOVER, right: 0, maxHeight: 220, overflowY: 'auto', listStyle: 'none' }}
    >
      {options.map((t) => (
        <li
          key={t}
          id={`${id}-${t}`}
          role="option"
          data-time={t}
          aria-selected={t === value}
          onClick={() => onPick(t)}
          style={{
            padding: '5px 8px',
            fontSize: 12.5,
            borderRadius: 6,
            cursor: 'pointer',
            color: 'var(--nb-ink)',
            background:
              t === active
                ? 'var(--nb-surface-2)'
                : t === value
                  ? 'var(--nb-primary-soft)'
                  : 'transparent',
          }}
        >
          {formatTimeLabel(t)}
        </li>
      ))}
    </ul>
  );
}
